// The Firebase modules the sign-in screens run on, loaded while the network
// is there.
//
// A browser keeps a failed dynamic import in its module map: until the page
// reloads, every later import() of that chunk, and of every chunk it pulls
// in, fails again without sending a request (Chromium on staging,
// 2026-10-15). Before the service worker controls the page (a first visit),
// one tap without a network would leave Criar conta, Entrar and the new
// password broken for the rest of the visit. So the modules are loaded as
// the app starts, and nothing here asks for them while the browser says it
// is offline: a refused load costs no chunk, and the next tap retries.

/** The modules could not be fetched: no network, or not yet. */
export class ModuleLoadError extends Error {
  readonly code = "app/module-load-failed";
  override name = "ModuleLoadError";
}

// firebase/functions and firebase/firestore too: sign-up sets the claim and
// stores the first name right after the account exists, with no tap between.
const importAll = () =>
  Promise.all([import("./firebase"), import("firebase/auth"), import("firebase/functions"), import("firebase/firestore")]);

let loading: ReturnType<typeof importAll> | null = null;

/** The client and the SDK modules; rejects with a ModuleLoadError when they cannot be fetched. */
export function loadFirebase(): ReturnType<typeof importAll> {
  if (loading) return loading;
  if (!navigator.onLine) return Promise.reject(new ModuleLoadError("offline, Firebase modules not requested"));
  loading = importAll().catch((error: unknown) => {
    loading = null;
    // A fetch that failed is a TypeError in every browser; anything else
    // (the blank config of HANDOFF F5) is no network problem.
    if (error instanceof TypeError) throw new ModuleLoadError("Firebase modules failed to load", { cause: error });
    throw error;
  });
  return loading;
}

/** At startup: loads the modules now, or once the network comes back. */
export function preloadFirebase(): void {
  const start = () => {
    loadFirebase().catch((error: unknown) => {
      console.error("[firebase] modules not preloaded", { kind: error instanceof Error ? error.name : typeof error });
    });
  };
  if (navigator.onLine) start();
  else window.addEventListener("online", start, { once: true });
}
